import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line
} from 'recharts';
import { AlertTriangle, CheckCircle, Clock, TrendingUp, Timer, RefreshCw, ShieldCheck } from 'lucide-react';
import api from '../services/api';

const statusLabel = {
  NUEVO: 'Nuevo', ASIGNADO: 'Asignadas', EN_PROCESO: 'En proceso',
  EN_ESPERA: 'En espera', PENDIENTE_VALIDACION: 'Pendiente validación', COMPLETADO: 'Completadas', CANCELADO: 'Cancelado'
};
const PRIORITY_COLOR = {
  BAJA:    { bg: '#f1f5f9', text: '#64748b' },
  MEDIA:   { bg: '#eff6ff', text: '#2563eb' },
  ALTA:    { bg: '#fff7ed', text: '#ea580c' },
  CRITICA: { bg: '#fef2f2', text: '#dc2626' },
};
const STATUS_COLOR = {
  NUEVO:      { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
  ASIGNADO:   { bg: '#fffbeb', text: '#d97706', border: '#fde68a' },
  EN_PROCESO: { bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' },
  EN_ESPERA:  { bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe' },
  PENDIENTE_VALIDACION: { bg: '#fdf4ff', text: '#a21caf', border: '#f5d0fe' },
  COMPLETADO: { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
  CANCELADO:  { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
};
const SEMAFORO = {
  verde:    { color: '#16a34a', bg: '#f0fdf4', label: '🟢' },
  amarillo: { color: '#d97706', bg: '#fffbeb', label: '🟡' },
  rojo:     { color: '#dc2626', bg: '#fef2f2', label: '🔴' },
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState(30);
  const navigate = useNavigate();

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/dashboard');
      setData(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12, color: '#64748b' }}>
      <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      Cargando...
    </div>
  );

  if (!data) return <p style={{ color: '#dc2626' }}>Error al cargar el dashboard.</p>;

  const { indicadores, recent, cargaPorArea, tendencia, cargaPorResponsable } = data;
  const tendenciaFiltrada = tendencia.slice(tendencia.length - periodo);

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      <style>{`
        .stat-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 12px; }
        .chart-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        @media (max-width: 768px) {
          .stat-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .chart-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2f6b', margin: 0 }}>Panel de Control</h1>
        <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Se actualiza automáticamente cada 30 segundos</p>
      </div>

      {/* FILA 1 — Indicadores */}
      <div className="stat-grid" style={{ marginBottom: 20 }}>
        <StatCard icon={AlertTriangle} iconBg="linear-gradient(135deg,#dc2626,#b91c1c)" label="Vencidas (+72h)" value={indicadores.totalVencidas} bg={indicadores.totalVencidas > 0 ? '#fef2f2' : 'white'} border={indicadores.totalVencidas > 0 ? '#fecaca' : '#e2e8f0'} valueColor={indicadores.totalVencidas > 0 ? '#dc2626' : '#1a2f6b'} />
        <StatCard icon={Clock} iconBg="linear-gradient(135deg,#d97706,#b45309)" label="Asignadas" value={indicadores.totalAsignadas} bg="white" border="#e2e8f0" />
        <StatCard icon={TrendingUp} iconBg="linear-gradient(135deg,#ea580c,#c2410c)" label="En proceso" value={indicadores.totalEnProceso} bg="white" border="#e2e8f0" />
        <StatCard icon={ShieldCheck} iconBg="linear-gradient(135deg,#a21caf,#86198f)" label="Pendiente validación" value={indicadores.totalPendienteValidacion} bg={indicadores.totalPendienteValidacion > 0 ? '#fdf4ff' : 'white'} border={indicadores.totalPendienteValidacion > 0 ? '#f5d0fe' : '#e2e8f0'} valueColor="#a21caf" />
        <StatCard icon={CheckCircle} iconBg="linear-gradient(135deg,#16a34a,#15803d)" label="Completadas" value={indicadores.totalCompletadas} bg="white" border="#e2e8f0" valueColor="#16a34a" />
        <StatCard icon={Timer} iconBg="linear-gradient(135deg,#7c3aed,#6d28d9)" label="Tiempo promedio" value={indicadores.tiempoPromedioHoras != null ? `${indicadores.tiempoPromedioHoras}h` : '—'} bg="white" border="#e2e8f0" valueColor="#7c3aed" />
      </div>

      {/* FILA 2 — Gráficas */}
      <div className="chart-grid" style={{ marginBottom: 20 }}>
        <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', padding: 20 }}>
          <h2 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: '#1a2f6b' }}>Carga por área (activas)</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={cargaPorArea} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis dataKey="area" type="category" tick={{ fontSize: 10 }} width={100} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="sinAsignar" name="Sin asignar" stackId="a" fill="#94a3b8" />
              <Bar dataKey="asignadas" name="Asignadas" stackId="a" fill="#f59e0b" />
              <Bar dataKey="enProceso" name="En proceso" stackId="a" fill="#3b82f6" />
              <Bar dataKey="vencidas" name="Vencidas" stackId="a" fill="#ef4444" radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1a2f6b' }}>Recibidas vs. completadas</h2>
            <div style={{ display: 'flex', gap: 4 }}>
              {[7, 30].map(p => (
                <button key={p} onClick={() => setPeriodo(p)} style={{ background: periodo === p ? '#2563eb' : 'white', color: periodo === p ? 'white' : '#64748b', border: '1.5px solid', borderColor: periodo === p ? '#2563eb' : '#e2e8f0', borderRadius: 6, padding: '3px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {p === 7 ? '7d' : '30d'}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={tendenciaFiltrada} margin={{ left: 0, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="fecha" tick={{ fontSize: 9 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip labelFormatter={d => `Fecha: ${d}`} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="recibidas" name="Recibidas" stroke="#2563eb" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="completadas" name="Completadas" stroke="#16a34a" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* FILA 3 — Solicitudes recientes */}
      <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1a2f6b' }}>Solicitudes recientes</h2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['', 'Folio', 'Descripción', 'Área', 'Responsable', 'Prioridad', 'Estatus', 'Tiempo'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr><td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>No hay solicitudes</td></tr>
              )}
              {recent.map(r => {
                const sem = SEMAFORO[r.semaforo] || SEMAFORO.verde;
                const sc = STATUS_COLOR[r.status] || {};
                const pc = PRIORITY_COLOR[r.priority] || {};
                return (
                  <tr key={r.id} onClick={() => navigate(`/solicitudes/${r.id}`)}
                    style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                  >
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{sem.label}</td>
                    <td style={{ padding: '10px 12px' }}><span style={{ fontFamily: 'monospace', fontSize: 10, color: '#2563eb', fontWeight: 700 }}>{r.folio}</span></td>
                    <td style={{ padding: '10px 12px', maxWidth: 200 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1e293b' }}>{r.description}</div></td>
                    <td style={{ padding: '10px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.area?.name || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.assignedTo?.name || 'Sin asignar'}</td>
                    <td style={{ padding: '10px 12px' }}><span style={{ background: pc.bg, color: pc.text, padding: '2px 6px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{r.priority}</span></td>
                    <td style={{ padding: '10px 12px' }}><span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: '2px 6px', borderRadius: 20, fontSize: 10, fontWeight: 600 }}>{statusLabel[r.status] || r.status}</span></td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}><span style={{ background: sem.bg, color: sem.color, padding: '2px 6px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{r.horasTranscurridas < 1 ? '<1h' : `${r.horasTranscurridas}h`}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* FILA 4 — Carga por responsable */}
      {cargaPorResponsable.length > 0 && (
        <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1a2f6b' }}>Carga por responsable</h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Responsable', 'Área', 'Activas', 'Vencidas', 'Tiempo promedio'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cargaPorResponsable.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                  >
                    <td style={{ padding: '11px 16px', fontWeight: 600, color: '#1e293b' }}>{r.nombre}</td>
                    <td style={{ padding: '11px 16px', color: '#64748b' }}>{r.area}</td>
                    <td style={{ padding: '11px 16px' }}><span style={{ background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{r.activas}</span></td>
                    <td style={{ padding: '11px 16px' }}>{r.vencidas > 0 ? <span style={{ background: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{r.vencidas} ⚠️</span> : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                    <td style={{ padding: '11px 16px', color: r.tiempoPromedio ? '#7c3aed' : '#94a3b8', fontWeight: r.tiempoPromedio ? 600 : 400 }}>{r.tiempoPromedio || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, iconBg, label, value, bg, border, valueColor = '#1a2f6b' }) {
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 14, padding: 16, boxShadow: '0 2px 10px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <Icon size={17} color="white" />
      </div>
      <div>
        <div style={{ fontSize: 26, fontWeight: 800, color: valueColor, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );
}
